/**
 * Membership page content layer
 * All static copy for the membership dashboard page lives here.
 * Organisms import from this file rather than hardcoding strings.
 */

// -----------------------------------------------------------------------------
// SEO & Page Meta
// -----------------------------------------------------------------------------

export const MEMBERSHIP_SEO = {
  title: 'Member Dashboard',
  description: 'Manage your Avow membership, view rewards, and track your insurance-backed benefits.',
}

// -----------------------------------------------------------------------------
// Snapshot Check-In
// -----------------------------------------------------------------------------

export const MEMBERSHIP_SNAPSHOT = {
  readOnlyNotice: 'Uploads need an active membership. Your score and documentation are kept.',
}

// -----------------------------------------------------------------------------
// Page hero
// -----------------------------------------------------------------------------

/** The head of the member dashboard: what the membership is and how it stands, in one line each. */
export const MEMBERSHIP_HERO = {
  kicker: 'Member dashboard',
  title: (tier: string) => `${tier} membership`,
  policy: (type: string) => `${type} policy`,
  /** One sentence per status, keyed like `getMembershipStatusConfig`. */
  status: {
    'active': (nextPayout: string | null) => nextPayout != null ? `You're earning. Next payout ${nextPayout}.` : 'You\'re earning.',
    'onboarding': (firstPayout: string | null) => firstPayout != null ? `Your first payout is on ${firstPayout}.` : 'Your first payout is on its way.',
    'action-required': 'Your membership needs attention before it pays out.',
    'suspended': 'Payouts are paused until your membership is reactivated.',
    'ended': 'Your membership has ended. Your documentation is kept.',
  },
  tiles: {
    deposit: 'Deposit',
    contributed: (amount: string) => `+${amount} contributed`,
    apy: 'APY',
    /** Trial deposits nothing, so it has no APY; it shows what a payout pays it. */
    perPayout: 'Per payout',
    active: 'Active',
    days: 'days',
    validThru: 'Valid thru',
    trialEndsIn: 'Trial ends in',
  },
}

// -----------------------------------------------------------------------------
// Rewards and Claims Readiness
// -----------------------------------------------------------------------------

/** How much more a check-in photo makes a member count for in a payout. Pinned to payoutRules by a test. */
const CHECK_IN_BOOST = '1.2×'

/** The rewards card: what is claimable, when the next payout comes, and whether the check-in boost is on. */
export const MEMBERSHIP_REWARDS = {
  title: 'Rewards',
  available: 'Available',
  nextPayout: 'Next payout',
  totalEarned: 'Total earned',
  toBeAnnounced: 'To be announced',
  nothingToClaim: 'Nothing to claim yet.',
  boost: {
    on: `Check-in boost on: you count ${CHECK_IN_BOOST} in the next payout.`,
    open: (timeLeft: string) => `Add a check-in photo to count ${CHECK_IN_BOOST} in the next payout. ${timeLeft} left.`,
  },
}

/**
 * The readiness card: the score, the public adjuster bar the tier sets, this period's
 * check-in and the photos worth taking next. Payout copy says "the next payout", never a
 * date: payout dates are figured in local time and check-in periods in UTC.
 */
export const MEMBERSHIP_READINESS = {
  title: 'Claims Readiness',
  boost: CHECK_IN_BOOST,
  scoreText: (score: number, level: string) => `${score} of 100, ${level}`,
  adjuster: {
    pointsToGo: (points: number, tier: string, threshold: number) =>
      `${points} more ${points === 1 ? 'point unlocks' : 'points unlock'} public adjuster review (${tier} needs ${threshold})`,
    unlocked: 'Public adjuster review is unlocked',
    anyScore: (tier: string) => `Public adjuster review is open at any score on ${tier}`,
  },
  period: {
    heading: 'This period',
    done: `Photo accepted. You count ${CHECK_IN_BOOST} in the next payout.`,
    open: `Add a photo to count ${CHECK_IN_BOOST} in the next payout.`,
    timeLeft: (time: string) => `${time} left`,
    upload: (title: string) => `Upload ${title}`,
    badge: 'This period',
  },
  next: {
    heading: 'Next photos',
    missing: 'Missing',
    age: (days: number) => `${days} ${days === 1 ? 'day' : 'days'} old`,
    countsHalf: 'counts half',
    countsQuarter: 'counts a quarter',
    points: (points: number) => `+${points} pts`,
    upload: (title: string) => `Upload ${title}`,
    refresh: (title: string) => `Refresh ${title}`,
  },
  allCurrent: (title: string, days: number) =>
    `Everything is current. ${title} starts counting half in ${days} ${days === 1 ? 'day' : 'days'}.`,
  summary: {
    current: (current: number, total: number) => `${current} of ${total} current`,
    aging: (count: number) => `${count} aging`,
    stale: (count: number) => `${count} stale`,
    missing: (count: number) => `${count} missing`,
    skipped: (count: number) => `${count} skipped`,
    openChecklist: 'Open checklist',
  },
  unavailable: {
    noPolicy: 'Your readiness score starts once your policy is approved.',
    noPrompts: 'There is nothing to document for your policy yet.',
  },
}

// -----------------------------------------------------------------------------
// Ended Membership
// -----------------------------------------------------------------------------

/**
 * What a member who ended their own membership sees, in place of the
 * "suspended" and "reactivate" copy shown when a membership lapses.
 */
export const MEMBERSHIP_ENDED = {
  statusLabel: 'Ended',
  bannerTitle: 'Membership Ended',
  bannerBody: 'You ended your membership. Your documentation is kept, and you can rejoin anytime with Rejoin Avow below.',
  sectionLabel: 'Rejoin',
  actionLabel: 'Rejoin Avow',
  actionShortLabel: 'Rejoin',
  rejoinTitle: 'Rejoin Avow',
  rejoinBody: 'Rejoining starts the wait before your first payout again.',
  /** Shown once the member confirms, while the rejoin runs and when it finishes. */
  processing: {
    title: 'Rejoining Avow',
    finalizingTitle: 'Finishing your rejoin',
    finalizingSubtitle: 'Your details are in. We are setting your membership up again.',
    successTitle: 'Rejoined',
    successText: 'Welcome back. Your membership is active again.',
    errorTitle: 'Rejoin failed',
  },
}

// -----------------------------------------------------------------------------
// Suspended Membership
// -----------------------------------------------------------------------------

/**
 * What a member whose membership lapsed (policy expired or rejected, deposit short)
 * sees on the banner and in the Reactivate flow, which is where both are fixed.
 */
export const MEMBERSHIP_REACTIVATE = {
  bannerTitle: 'Membership Suspended',
  bannerFallback: 'Restore your perks by resolving the issues below.',
  bannerPolicyExpired: (date: string) => `Your insurance policy expired on ${date}.`,
  bannerPolicyMissing: 'Your membership needs an approved insurance policy.',
  bannerDepositShort: (amount: number) => `Your deposit is below the $${amount.toLocaleString()} minimum.`,
  bannerNext: 'Reactivate to upload a current policy and get your benefits back.',
  bannerAction: 'REACTIVATE: UPLOAD POLICY',
  title: 'Reactivate Membership',
  tierLabel: 'Choose your tier',
  policyExpired: (date: string) => `Expired on ${date}. Upload your current policy below.`,
  /** Shown when the new document is uploaded but its review has not finished. */
  pendingReviewTitle: 'Document under review',
  pendingReview: 'Your document is uploaded and being reviewed. Once it is approved, come back and press Reactivate to finish.',
}

/**
 * The checklist on the Reactivate, Upgrade and Downgrade screens: the policy and the
 * deposit the chosen tier needs, each met or still to do.
 */
export const MEMBERSHIP_REQUIREMENTS = {
  title: 'What you need',
  newTierLabel: 'Choose your new tier',
  policyLabel: 'Insurance policy',
  policyMet: 'Approved and in date.',
  policyCovers: (type: string, tier: string) => `Your ${type} policy covers ${tier}.`,
  policyMissing: 'Upload your current policy below.',
  policyWrongType: (type: string, tier: string) => `Your ${type} policy does not cover ${tier}. Upload a policy that does, or choose another tier.`,
  policyRejected: 'Your last document was not accepted. Upload a new one below.',
  policyInReview: 'Your last upload is still being reviewed. Come back once it is approved, or upload a different document below.',
  policyChosen: (fileName: string) => `${fileName} will be reviewed when you continue.`,
  /** Update Insurance, with an approved policy already on file. */
  policyOnFile: (type: string | null, tier: string, until: string | null) =>
    `Your ${type ?? 'approved'} policy covers ${tier}${until != null ? ` until ${until}` : ''}. Upload a renewal or replacement below.`,
  depositLabel: 'Deposit',
  depositMet: (tier: string, required: string, held: string) => `${tier} needs ${required}. Your ${held} covers it.`,
  depositShort: (tier: string, required: string, held: string) => `${tier} needs ${required} and you hold ${held}.`,
  /** A covered top-up, made by Continue once everything else is in place. */
  depositOnContinue: (amount: string) => `Your wallet covers the ${amount}, deposited when you continue.`,
  depositSurplus: (tier: string, required: string, held: string, surplus: string) => `${tier} needs ${required} and you hold ${held}. The ${surplus} above it goes back to your wallet when you continue.`,
  withdrawLabel: 'Withdraw',
  withdrawAvailable: (held: string, available: string) => `You hold ${held} and can withdraw ${available}.`,
  /** After an approved claim the minimum is waived for a while, then due again. */
  withdrawExemption: (days: number, tier: string, required: string) =>
    `Your claim exemption ends in ${days} ${days === 1 ? 'day' : 'days'}. Hold ${tier}’s ${required} by then to stay a member.`,
  /** Outside an exemption only the part above the minimum can leave the vault. */
  withdrawAboveMinimum: (tier: string, required: string) => `That is what sits above ${tier}’s ${required} minimum.`,
  withdrawNone: 'Nothing can be withdrawn right now.',
  /** Beside Get USDC when the wallet cannot cover the top-up. */
  walletShort: (available: string) => `${available} USDC in your wallet.`,
  confirm: 'Continue',
  confirmTopUp: (amount: string, verb: string) => `Deposit ${amount} and ${verb}`,
  confirmWithdraw: (amount: string) => `Downgrade and withdraw ${amount}`,
  /** The picker's hint: which policies the tier takes and which files are read. */
  uploadHint: (types: string, maxMb: number) => `${types} policy · PDF or image up to ${maxMb}MB`,
}

/** The deposit Continue makes for an upgrade or reactivation, while the wallet confirms it and if it fails. */
export const MEMBERSHIP_TOP_UP = {
  awaitingTitle: 'Waiting for your deposit',
  awaitingSubtitle: 'Your wallet is confirming the deposit. This usually takes a few seconds.',
  notConfirmed: 'Your wallet has not confirmed the deposit after two minutes. Nothing else has changed. Check your wallet’s activity before continuing again.',
  reverted: 'Your deposit did not go through, so nothing has changed. Try again.',
}

/** What Continue reports when a downgrade moves money on the way. */
export const MEMBERSHIP_TIER_CHANGE = {
  downgradedAndWithdrawn: (tier: string, amount: string) => `You are now on ${tier}, and ${amount} is back in your wallet.`,
  withdrawAfterDowngradeFailed: (tier: string, amount: string) =>
    `You are now on ${tier}, but the ${amount} withdrawal did not go through. Use Withdraw Funds to take it out.`,
}

/**
 * The way forward when the server refuses a trial because the member's policy has
 * already had one. Matched on the server's wording, which names the free trial.
 */
export const TRIAL_ALREADY_USED = {
  pattern: /already had its free trial/i,
  nextSteps: [
    'Each insurance policy gets one free trial, and this one has had it.',
    'Choose Bronze, Silver or Gold instead. Each needs its tier’s deposit, and your approved policy still counts.',
  ],
}

// -----------------------------------------------------------------------------
// Documentation Export
// -----------------------------------------------------------------------------

export const MEMBERSHIP_DOCUMENTATION_EXPORT = {
  title: 'Your Documentation',
  description: 'Your proof of insurance, check-in photos and claim documents in one zip file, with a spreadsheet listing each one and the details read from it. You can download it any time, including after your membership ends.',
  button: 'Download Everything',
  preparing: 'Gathering your files…',
  downloading: (done: number, total: number) => `Downloading ${done} of ${total} files…`,
  done: 'Your zip file is in your downloads.',
  empty: 'Nothing to download yet. Your photos and documents will be here once you upload them.',
  failures: {
    prepare: 'We couldn’t gather your files. Try again in a moment.',
    download: 'A file didn’t finish downloading. Check your connection and try again.',
  },
}
